"use client";

import * as React from "react";
import { ShieldCheck, Lock, FileText, Info, Loader } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
const NativeInput = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(({
  className,
  ...props
}, ref) => {
  return <input className={`flex h-12 w-full rounded-[2px] border border-white/15 bg-white/5 px-3 py-2 text-sm text-foreground ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-white/30 disabled:cursor-not-allowed disabled:opacity-50 ${className}`} ref={ref} {...props} />;
});
NativeInput.displayName = "NativeInput";
const NativeSelect = React.forwardRef<HTMLSelectElement, React.SelectHTMLAttributes<HTMLSelectElement>>(({
  className,
  children,
  ...props
}, ref) => {
  return <div className="relative">
        <select className={`flex h-12 w-full appearance-none rounded-[2px] border border-white/15 bg-white/5 px-3 py-2 text-sm text-foreground ring-offset-background focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-white/30 disabled:cursor-not-allowed disabled:opacity-50 ${className}`} ref={ref} {...props}>
          {children}
        </select>
        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground">
          <svg className="h-4 w-4 fill-current" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
            <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
          </svg>
        </div>
      </div>;
});
NativeSelect.displayName = "NativeSelect";
const NativeCheckbox = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(({
  className,
  ...props
}, ref) => {
  return <input type="checkbox" className={`peer h-4 w-4 shrink-0 rounded-[2px] border border-white/30 bg-transparent text-primary ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${className}`} ref={ref} {...props} />;
});
NativeCheckbox.displayName = "NativeCheckbox";
function RiskAdvisoryForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center space-y-4 rounded-[2px] border border-white/10 bg-white/5 p-12 text-center animate-in fade-in zoom-in duration-500">
        <div className="rounded-full bg-white/10 p-4">
          <ShieldCheck className="h-10 w-10 text-white" />
        </div>
        <h3 className="text-2xl font-medium text-white">Inquiry Received</h3>
        <p className="max-w-md text-muted-foreground">{successMessage}</p>
        <Button variant="outline" className="mt-4 border-white/20 text-white hover:bg-white/10 hover:text-white" onClick={() => window.location.reload()}>
          Submit Another Inquiry
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <div className="grid gap-6 md:grid-cols-2">
        {}
        <FormField control={control} name="full_name" rules={{
        required: "Full Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-white/80">Full Name</FormLabel>
              <FormControl>
                <NativeInput placeholder="Jane Doe" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="company_name" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-white/80">Company / Organization</FormLabel>
              <FormControl>
                <NativeInput placeholder="Dynestate Holdings" {...field} />
              </FormControl>
              <FormDescription className="text-xs text-white/40">Optional</FormDescription>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="email_address" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-white/80">Email Address</FormLabel>
              <FormControl>
                <NativeInput type="email" placeholder="jane.doe@example.com" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="phone_number" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-white/80">Phone Number</FormLabel>
              <FormControl>
                <NativeInput type="tel" placeholder="+1 (555) 123-4567" {...field} />
              </FormControl>
              <FormDescription className="text-xs text-white/40">Include country code</FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      {}
      <FormField control={control} name="advisory_type" rules={{
      required: "Please select a risk category"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-white/80">Risk Advisory Type</FormLabel>
            <FormControl>
              <NativeSelect {...field}>
                <option value="" disabled>
                  Select a risk category
                </option>
                <option value="Credit Risk">Credit Risk</option>
                <option value="Market Risk">Market Risk</option>
                <option value="Operational Risk">Operational Risk</option>
                <option value="Regulatory Compliance">Regulatory Compliance</option>
                <option value="Other">Other</option>
              </NativeSelect>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {}
      <FormField control={control} name="inquiry_details" rules={{
      required: "Please provide details about your inquiry"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-white/80">Inquiry Details</FormLabel>
            <FormControl>
              <Textarea placeholder="Provide a brief description of your risk advisory needs, objectives, and any specific concerns." className="min-h-[160px] rounded-[2px] border-white/15 bg-white/5 text-white placeholder:text-muted-foreground focus-visible:ring-white/30" {...field} />
            </FormControl>
            <FormDescription className="text-white/40">
              The more detail you provide, the better we can tailor our advisory services.
            </FormDescription>
            <FormMessage />
          </FormItem>} />

      {}
      <FormField control={control} name="agree_privacy" rules={{
      required: "You must agree to the privacy notice"
    }} render={({
      field: {
        value,
        onChange,
        ...field
      }
    }) => <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-[2px] border border-white/10 bg-white/5 p-4">
            <FormControl>
              <NativeCheckbox checked={value} onChange={e => onChange(e.target.checked)} {...field} className="mt-1 border-white/40 data-[state=checked]:bg-white data-[state=checked]:text-black" />
            </FormControl>
            <div className="space-y-1 leading-none">
              <FormLabel className="text-sm font-normal text-white/90">
                I agree to the Privacy Notice
              </FormLabel>
              <FormDescription className="text-xs text-white/50">
                We respect your privacy and will only use this information to respond to your inquiry.
              </FormDescription>
            </div>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="flex items-center gap-2 rounded-[2px] border border-red-900/50 bg-red-950/30 p-4 text-red-400">
          <Info className="h-4 w-4" />
          <p className="text-sm">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="h-14 w-full rounded-[2px] bg-white text-base font-medium text-black hover:bg-white/90 sm:w-auto sm:px-10">
        {isSubmitting ? <>
            <Loader className="mr-2 h-4 w-4 animate-spin" />
            Processing...
          </> : "Connect with Advisor"}
      </Button>
    </Form>;
}
export default function RiskAdvisoryInquiry() {
  return <section id="risk-advisory-inquiry" className="dark bg-background text-foreground relative overflow-hidden py-24 sm:py-32">
      {}
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_var(--tw-gradient-stops))] from-white/5 via-transparent to-transparent opacity-40" />
        <div className="absolute bottom-0 left-0 h-[500px] w-[500px] bg-white/5 blur-[120px] opacity-20" />
      </div>

      <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mx-auto grid max-w-7xl gap-16 lg:grid-cols-12">
          {}
          <div className="lg:col-span-5 lg:pt-8">
            <div className="sticky top-24 space-y-8">
              <div className="space-y-4">
                <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs font-medium uppercase tracking-wider text-white/70">
                  <Lock className="h-3 w-3" />
                  <span>Secure Advisory Channel</span>
                </div>
                <h2 className="font-serif text-4xl font-medium tracking-tight text-white sm:text-5xl">
                  Risk Advisory Inquiry
                </h2>
                <p className="text-lg leading-relaxed text-muted-foreground">
                  Engage with our specialized risk management team to structure protection for your institutional assets.
                  We provide bespoke analysis for credit, market, and operational exposure.
                </p>
              </div>

              <div className="space-y-6 border-t border-white/10 pt-8">
                <div className="flex gap-4">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-[2px] border border-white/10 bg-white/5">
                    <FileText className="h-5 w-5 text-white/70" />
                  </div>
                  <div>
                    <h3 className="text-base font-medium text-white">Confidential Assessment</h3>
                    <p className="mt-1 text-sm text-muted-foreground">
                      All inquiries are handled with strict confidentiality and routed directly to senior advisors.
                    </p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-[2px] border border-white/10 bg-white/5">
                    <ShieldCheck className="h-5 w-5 text-white/70" />
                  </div>
                  <div>
                    <h3 className="text-base font-medium text-white">Institutional Standards</h3>
                    <p className="mt-1 text-sm text-muted-foreground">
                      Our advisory protocols align with global institutional compliance and risk frameworks.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {}
          <div className="lg:col-span-7">
            <div className="rounded-[2px] border border-white/10 bg-black/40 p-6 backdrop-blur-sm sm:p-10">
              <FormProvider formId="risk_advisory_inquiry_credit_protection" sectionName="risk-advisory-inquiry" defaultValues={{
              full_name: "",
              company_name: "",
              email_address: "",
              phone_number: "",
              advisory_type: "",
              inquiry_details: "",
              agree_privacy: false
            }} validationRules={{
              full_name: {
                required: true
              },
              email_address: {
                required: true,
                pattern: "email"
              },
              advisory_type: {
                required: true
              },
              inquiry_details: {
                required: true
              },
              agree_privacy: {
                required: true
              }
            }} submitText="Connect with Advisor" successMessage="Your inquiry has been securely transmitted. A senior risk advisor will review your details and contact you shortly.">
                <RiskAdvisoryForm />
              </FormProvider>
            </div>
          </div>
        </div>
      </div>
    </section>;
}